import React, { useState, useRef, useEffect } from 'react';
import question_icon from 'src/images/icon/question_icon.png';

interface FormFieldSingleAutocompleteDropdownProps {
    label: string;
    name: string;
    value?: string;
    onChange: (selectedValue: string) => void;
    options: string[];
    className?: string;
    warning?: string;
    infoMessage?: string;
    placeholder?: string;
    error?: boolean;
    errorMessage?: string;
    onBlur?: () => void;
    showNotProvidedExplanation?: boolean;
    notProvidedExplanation?: string;
}

const FormFieldSingleAutocompleteDropdown: React.FC<FormFieldSingleAutocompleteDropdownProps> = ({
    label,
    name,
    value = '',
    onChange,
    options,
    className = 'form-field',
    warning,
    infoMessage,
    placeholder = 'Select an option',
    error,
    errorMessage,
    onBlur,
    showNotProvidedExplanation,
    notProvidedExplanation
}) => {
    const [showInfo, setShowInfo] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                if (onBlur) onBlur();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onBlur]);

    const handleOptionSelect = (option: string) => {
        onChange(option);
        setSearchText('');
        setIsOpen(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
        setIsOpen(true);
    };

    const displayText = isOpen ? searchText : value || '';
    const filteredOptions = options.filter(option => 
        option.toLowerCase().includes(searchText.toLowerCase())
    );

    return (
        <div className={className}>
            <div className="form-label-row">
                <label className="form-label">{label}</label>
                {infoMessage && (
                    <div
                        style={{ position: 'relative', display: 'inline-block' }}
                        onMouseEnter={() => setShowInfo(true)}
                        onMouseLeave={() => setShowInfo(false)}
                    >
                        <img
                            src={question_icon}
                            alt="Info"
                            className="question-icon"
                        />
                        {showInfo && (
                            <div className="info-message tooltip">
                                {infoMessage}
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div className="multi-select-dropdown-container" ref={containerRef}>
                <div className="form-input-wrapper">
                    <input
                        type="text"
                        name={name}
                        value={displayText}
                        placeholder={placeholder}
                        className={`form-input multi-select-input ${error ? 'form-input-error' : ''}`}
                        onChange={handleInputChange}
                        onFocus={() => setIsOpen(true)}
                        style={{ paddingRight: '30px' }}
                    />
                    <span 
                        className="dropdown-arrow"
                        onClick={() => setIsOpen(!isOpen)}
                        style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            cursor: 'pointer',
                            fontSize: '12px',
                            color: '#666'
                        }}
                    >
                        ⌄
                    </span>
                </div>
                {isOpen && (
                    <div className="multi-select-dropdown">
                        {filteredOptions.map(option => (
                            <div
                                key={option}
                                className="multi-select-option"
                                onClick={() => handleOptionSelect(option)}
                                style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    backgroundColor: value === option ? '#f0f0f0' : 'white'
                                }}
                            >
                                <span>{option}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {showNotProvidedExplanation && <div className="option-not-provided-explanation">{notProvidedExplanation}</div>}
            {warning && <div className="email-warning">{warning}</div>}
            {error && errorMessage && <div className="field-error">{errorMessage}</div>}
        </div>
    );
};

export default FormFieldSingleAutocompleteDropdown;